import { array, bool, func, string, object } from 'prop-types'
import { Component } from 'react'
import { getTrackableComponent } from '@opallabs/quanta'
import { kuiperClassNames } from '../utils'
import StepSideBar from './StepSideBar'

import './styles.scss'

const PREVIEW_AREA_CLASS = 'scrollable-preview-area'
const MENU_SECTION_CLASS = 'scrollable-section'
const PREVIEW_AREA_MATCHER = new RegExp(
  `${kuiperClassNames(PREVIEW_AREA_CLASS)}`
)

class ScrollableMenu extends Component {
  static propTypes = {
    className: string,
    disableHighlighting: bool,
    menuSections: array,
    previewArea: func,
    previewerCustomClassNames: array,
    secondaryTitle: object,
    title: string,
    trackers: object,
  }

  static defaultProps = {
    menuSections: [],
    previewerCustomClassNames: [],
    trackers: {},
  }

  constructor(props) {
    super(props)
    this.state = {
      activeSection: 0,
      disableHighlighting: props.disableHighlighting || false,
    }

    /*
    will be an object containing data like below
    initially, only the ref callbacks are added to the value objects
    when the sections are added to the DOM the callbacks are exectuted,
    removed from the object, and replaced with the element
    {
      '1': { refCallback: [Function: ref], element: ref },
      '2': { refCallback: [Function: ref], element: ref },
      '3': { refCallback: [Function: ref], element: ref },
    }
    */
    this.aggregateSectionData = {}

    this.scrollTimeout()
    this.buildRefs()
  }

  get track() {
    return this.props.trackers.track
  }

  doTracking = (label, val) => {
    if (this.track) {
      this.track(label, val)
    }
  }

  renderSections() {
    return this.props.menuSections.map((section) => {
      if (!section) {
        return null
      }

      const key = section.key

      return (
        <section
          className={kuiperClassNames(
            MENU_SECTION_CLASS,
            section.props.customClassName
          )}
          key={key}
          ref={this.aggregateSectionData[key].refCallback}
        >
          {section}
        </section>
      )
    })
  }

  buildRefs() {
    this.props.menuSections.forEach((section) => {
      if (!section) {
        return
      }

      const key = section.key
      const ref = (element) => {
        this.aggregateSectionData[key] = { element }
      }

      this.aggregateSectionData[key] = {
        ...this.aggregateSectionData[key],
        refCallback: ref,
      }
    })
  }

  newSectionsIncoming() {
    if (this.props.menuSections.length !== this.aggregateSectionData.length) {
      return true
    }

    return this.props.menuSections.some(
      (section) => !this.aggregateSectionData[section.key]
    )
  }

  sectionElements() {
    return this.props.menuSections.map((section) => {
      if (!section) {
        return null
      }
      return this.aggregateSectionData[section.key].element
    })
  }

  setActiveSection = (index) => {
    this.setState({ activeSection: index })
  }

  scrollTimeout() {
    return setTimeout(() => {
      this.setState({ disableHighlighting: false })
    }, 250)
  }

  cacheMenuElement = ({ target }) => {
    if (target.className.match(PREVIEW_AREA_MATCHER)) {
      this.setState({ menuEl: target })
    }
  }

  get previewerClassNames() {
    return this.props.previewerCustomClassNames.concat(PREVIEW_AREA_CLASS)
  }

  highlightActiveSection = () => {
    this.setState((state) => {
      const shouldNotHighlight = state.disableHighlighting || !state.menuEl

      if (shouldNotHighlight) return null

      const bottomPosition =
        state.menuEl.scrollHeight - state.menuEl.clientHeight
      const atBottomOfMenu =
        Math.abs(bottomPosition - state.menuEl.scrollTop) < 5
      const lastSectionIndex = Object.keys(this.aggregateSectionData).length - 1
      const topSectionIndex = this.sectionElements().findIndex((element) => {
        const offsetBottom =
          (element && element.offsetTop) + (element && element.offsetHeight)
        const elementTopAboveView =
          (element && element.offsetTop) <= state.menuEl.scrollTop
        const elementBottomInView = offsetBottom > state.menuEl.scrollTop

        return elementTopAboveView && elementBottomInView
      })

      let newIndex =
        topSectionIndex === -1 ? state.activeSection : topSectionIndex

      if (atBottomOfMenu) {
        newIndex = lastSectionIndex
      }

      return { activeSection: newIndex }
    })
  }

  render() {
    if (this.newSectionsIncoming()) {
      this.buildRefs()
    }

    return (
      <div
        className={kuiperClassNames('scrollable-menu', this.props.className)}
      >
        <StepSideBar
          title={this.props.title}
          secondaryTitle={this.props.secondaryTitle}
          activeSection={this.state.activeSection}
          sectionElements={this.sectionElements()}
          setActiveSection={this.setActiveSection}
          setDisableHighlighting={this.setDisableHighlighting}
          doTracking={this.doTracking}
        >
          {this.props.menuSections}
        </StepSideBar>
        {this.props.previewArea(
          this.renderSections(),
          this.previewerClassNames,
          {
            onWheel: this.highlightActiveSection,
            onScroll: this.cacheMenuElement,
          }
        )}
      </div>
    )
  }
}

export const TrackedScrollableMenu = getTrackableComponent(ScrollableMenu)
export default ScrollableMenu
